import React, { ComponentPropsWithoutRef, useMemo } from 'react';
import classnames from 'classnames';
import { CheckCheck, ClipboardCheck, Clock, CookingPot, Package, Truck, X } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import collect from 'collect.js';
import { Order } from '../../services/exports/Interfaces';
import { ORDER_FULFILLMENT_STATUSES, ORDER_METHODS } from '../../services/exports/Constants';
import { selectOngoingOrder } from '../../store/slices/OrderSlice';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { isOnSiteMethod } from '../../utils/on-site';

const TABLE_ORDER_SERVED_STEP = 'served' as const;

type OrderTrackerProps = ComponentPropsWithoutRef<'section'> & { order?: Order };

const OrderTracker = (props: OrderTrackerProps) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Order:OrderTracker' });
  const defaultOrder = useSelector(selectOngoingOrder);
  const ongoingOrder = props?.order ?? defaultOrder;
  const isRejected = ongoingOrder?.fulfillment?.status === ORDER_FULFILLMENT_STATUSES.REJECTED;
  const isPicked = ongoingOrder?.fulfillment?.status === ORDER_FULFILLMENT_STATUSES.PICKED;

  const isChecked = (status: ORDER_FULFILLMENT_STATUSES) => {
    if (isPicked) {
      return true;
    }

    return ongoingOrder?.fulfillment?.events.some((event) => event.status === status);
  };

  const isReadyChecked =
    isChecked(ORDER_FULFILLMENT_STATUSES.READY) || isChecked(ORDER_FULFILLMENT_STATUSES.ON_DELIVERY);
  const isPreparingChecked = isChecked(ORDER_FULFILLMENT_STATUSES.PREPARING);
  const isAcceptedChecked = isChecked(ORDER_FULFILLMENT_STATUSES.ACCEPTED);
  const isDelivery = ongoingOrder?.method === ORDER_METHODS.DELIVERY;
  // Staff bring a table order to the table: its last step is Served, not Ready.
  const readyTitle = isDelivery
    ? ORDER_FULFILLMENT_STATUSES.ON_DELIVERY
    : isOnSiteMethod(ongoingOrder?.method)
      ? TABLE_ORDER_SERVED_STEP
      : ORDER_FULFILLMENT_STATUSES.READY;

  const steps = useMemo<Step[]>(() => {
    const rawSteps: Step[] = [
      {
        status: ORDER_FULFILLMENT_STATUSES.CREATED,
        show: true,
        checked: true,
        icon: Package,
        title: ORDER_FULFILLMENT_STATUSES.CREATED,
      },
      {
        show: !isRejected,
        status: ORDER_FULFILLMENT_STATUSES.ACCEPTED,
        checked: isAcceptedChecked,
        icon: CheckCheck,
        title: ORDER_FULFILLMENT_STATUSES.ACCEPTED,
      },
      {
        show: isRejected,
        status: ORDER_FULFILLMENT_STATUSES.REJECTED,
        checked: true,
        icon: X,
        title: ORDER_FULFILLMENT_STATUSES.REJECTED,
        danger: true,
      },
      {
        status: ORDER_FULFILLMENT_STATUSES.PREPARING,
        show: true,
        checked: isPreparingChecked,
        icon: CookingPot,
        title: ORDER_FULFILLMENT_STATUSES.PREPARING,
      },
      {
        status: ORDER_FULFILLMENT_STATUSES.READY,
        show: true,
        checked: isReadyChecked,
        icon: isDelivery ? Truck : ClipboardCheck,
        title: readyTitle,
      },
    ].filter((step) => step && step.show);

    let lastCheckedIndex = -1;

    for (let i = 0; i < rawSteps.length; i++) {
      if (rawSteps[i].checked) {
        lastCheckedIndex = i;
      }
    }

    return rawSteps.map((step, index) => ({ ...step, checked: index <= lastCheckedIndex }));
  }, [isRejected, isAcceptedChecked, isPreparingChecked, isReadyChecked, readyTitle, isDelivery]);

  const activeStep = useMemo(
    () =>
      collect(steps)
        .filter((step) => step.checked)
        .last(),
    [steps],
  );

  const pendingStep = useMemo(() => collect(steps).first((step) => !step.checked) ?? null, [steps]);

  return (
    <section className={classnames('space-y-md', props.className)}>
      <div className="flex items-start w-full">
        {steps.map((step, index) => {
          const isLast = index === steps.length - 1;
          const Icon = step.icon;
          const isActive = activeStep?.status === step.status;

          return (
            <React.Fragment key={step.status}>
              <div className="flex flex-col items-center gap-2xs shrink-0">
                <div
                  className={classnames(
                    'relative w-9 h-9 rounded-full flex items-center justify-center transition-colors',
                    {
                      'bg-success text-brand-button-primary-text': step.checked && !step.danger,
                      'bg-danger text-brand-button-primary-text': step.checked && step.danger,
                      'bg-muted text-muted-foreground': !step.checked,
                    },
                  )}
                >
                  <Icon className="w-4 h-4" />
                  {isActive && (
                    <span
                      className={classnames('absolute inset-0 rounded-full ring-[4px] opacity-20 animate-pulse', {
                        'ring-danger': step.danger,
                        'ring-success': !step.danger,
                      })}
                    />
                  )}
                </div>
                <span
                  className={classnames('text-[11px] font-medium whitespace-nowrap', {
                    'text-brand-text-primary': step.checked,
                    'text-muted-foreground': !step.checked,
                  })}
                >
                  {t(step.title)}
                </span>
              </div>
              {!isLast && (
                <div className="flex-1 mx-xs mt-[18px]">
                  {!isRejected && isActive ? (
                    <div className="progress-container !h-0.5 !rounded-full !bg-muted">
                      <div className="progress-fill !rounded-full" />
                    </div>
                  ) : (
                    <div
                      className={classnames('h-0.5 rounded-full', {
                        'bg-danger': isRejected && step.checked,
                        'bg-success': !isRejected && step.checked,
                        'bg-muted': !step.checked,
                      })}
                    />
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div className="pt-sm border-t-xs border-brand-border flex items-center justify-between gap-sm">
        <div className="min-w-0">
          <p className={classnames('text-sm', { 'text-danger': isRejected, 'text-brand-text-primary': !isRejected })}>
            <span className="font-semibold">{t('status.actual')}:</span> {t(activeStep?.title)}
          </p>
          {!isRejected && pendingStep && (
            <p className="text-xs text-muted-foreground">
              {t('status.pending')}: {t(pendingStep?.title)}
            </p>
          )}
        </div>
        {!isRejected && <OrderTrackerTimeBar order={ongoingOrder} />}
      </div>
    </section>
  );
};

const OrderTrackerTimeBar = ({ order }: OrderTrackerProps) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Order:OrderTracker' });
  const { formatTime, formatDateTime } = useAppMethods();

  const timeInterval = useMemo(() => {
    if (order?.fulfillment?.status === ORDER_FULFILLMENT_STATUSES.REJECTED) {
      return null;
    }

    if (!order?.fulfillment?.estimated_pickup_time) {
      return null;
    }

    const estimatedPickupTime = moment(order?.fulfillment?.estimated_pickup_time);
    const min = formatTime(estimatedPickupTime);
    const max = formatTime(estimatedPickupTime.clone().add(10, 'minutes'));

    return `${min} – ${max}`;
  }, [order?.fulfillment?.estimated_pickup_time, order?.fulfillment?.status, formatTime]);

  const dayLabel = useMemo(() => {
    const reference = order?.fulfillment?.estimated_pickup_time ?? order?.scheduled_for;

    if (!reference) {
      return null;
    }

    return moment().isSame(reference, 'day')
      ? t('labels.today')
      : formatDateTime(reference, { format: 'dddd, DD MMM' });
  }, [order?.fulfillment?.estimated_pickup_time, order?.scheduled_for, formatDateTime, t]);

  if (!timeInterval) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2xs items-end">
      {dayLabel && <span className="text-[11px] text-muted-foreground">{dayLabel}</span>}
      <div className="flex items-center gap-2xs text-xs text-muted-foreground shrink-0">
        <Clock className="w-3.5 h-3.5" />
        <span className="tabular-nums">{timeInterval}</span>
      </div>
    </div>
  );
};

type Step = {
  status: ORDER_FULFILLMENT_STATUSES;
  show: boolean;
  checked: boolean;
  icon: typeof Package;
  title: ORDER_FULFILLMENT_STATUSES | typeof TABLE_ORDER_SERVED_STEP;
  danger?: boolean;
};

export default OrderTracker;
